import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';

// Using country-flags CDN for reliable flag images
const languages = [
  { code: 'en', name: 'English', flag: 'GB' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'zh', name: '中文', flag: 'CN' },
  { code: 'ar', name: 'العربية', flag: 'LB', rtl: true },
  { code: 'pt', name: 'Português', flag: 'BR' }
];

const FlagImage = ({ code, size = 20 }) => (
  <img 
    src={`https://flagsapi.com/${code}/flat/64.png`}
    width={size}
    height={size}
    alt=""
    className="rounded-sm object-contain"
    style={{ minWidth: size }}
    onError={(e) => {
      e.target.style.display = 'none';
    }}
  />
);

const LanguageSelector = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-selector-btn"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all text-sm"
      >
        <FlagImage code={currentLang.flag} size={18} />
        <span className="text-white font-medium">{currentLang.code.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 text-white/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              data-testid={`lang-option-${lang.code}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/10 transition-colors ${
                i18n.language === lang.code ? 'bg-primary/20 text-primary' : 'text-white'
              }`}
            >
              <FlagImage code={lang.flag} size={24} />
              <span className="flex-1 text-left">{lang.name}</span>
              {i18n.language === lang.code && <Check className="w-4 h-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
